/**
 * Geometry for report compositions — the single source of truth shared by the
 * canvas builder, the on-screen renderer, and the PDF.
 *
 * A `ReportSectionConfig` may carry a `layout` ({x,y,w,h} in grid units). Three
 * pure helpers turn that (or its absence) into something every consumer agrees
 * on:
 *
 *   - `deriveDefaultLayout` / `ensureLayout` place any section that lacks a
 *     `layout` as a full-width block, stacked in array order. So a composition
 *     authored before the grid builder existed behaves exactly as it did — a
 *     single-column stack — and a half-placed composition is completed without
 *     disturbing the sections an admin already positioned.
 *
 *   - `linearizeSections` is the parity primitive. Reading order is ALWAYS
 *     `(y, x)` (top-to-bottom, then left-to-right), with the original array
 *     index as the tiebreaker and the fallback when a section has no `layout`.
 *     The screen grid and the PDF both order themselves through this one
 *     function, so "screen and PDF agree by construction" is preserved as an
 *     ordering guarantee rather than a pixel one. For a composition with no
 *     geometry at all it returns the original array order verbatim.
 *
 * Pure module — no React, no rendering. Unit-tested in `layout.test.ts`.
 */
import type { ReportConfig, ReportSectionConfig, SectionLayout } from './types';

/** Columns the canvas and both renderers lay out against. */
export const GRID_COLS = 12;

/** Default row height (px) one grid row represents, shared by canvas + screen. */
export const GRID_ROW_HEIGHT = 30;

/**
 * Per-type starting size for a freshly placed section, in grid units. Every
 * default is full width (`w: GRID_COLS`) so a new section reads like the old
 * stacked list until an admin narrows or pairs it; heights are rough guesses
 * sized to typical content and are freely resized in the builder afterward.
 */
export const DEFAULT_SECTION_SIZE: Record<string, { w: number; h: number }> = {
  scoreHeader: { w: GRID_COLS, h: 4 },
  narrative: { w: GRID_COLS, h: 5 },
  threatBars: { w: GRID_COLS, h: 8 },
  radar: { w: GRID_COLS, h: 10 },
  heatmap: { w: GRID_COLS, h: 10 },
  recommendations: { w: GRID_COLS, h: 12 },
  table: { w: GRID_COLS, h: 8 },
  chipGroups: { w: GRID_COLS, h: 6 },
  architecture: { w: GRID_COLS, h: 10 },
  accordionList: { w: GRID_COLS, h: 8 },
  cardGrid: { w: GRID_COLS, h: 10 },
};

const FALLBACK_SIZE = { w: GRID_COLS, h: 6 };

/**
 * A deep-enough clone of a composition for local editing: copies each section
 * plus its `options` and `layout` so the working copy never aliases the saved
 * one. (The old `ReportBuilder` clone predated geometry and dropped `layout` —
 * use this everywhere the canvas edits a config.)
 */
export function cloneReportConfig(config: ReportConfig): ReportConfig {
  return {
    ...config,
    sections: config.sections.map((s) => ({
      ...s,
      options: s.options ? { ...s.options } : undefined,
      layout: s.layout ? { ...s.layout } : undefined,
    })),
  };
}

/** Starting size for a section `type`, falling back for unknown types. */
export function defaultSizeForType(type: string): { w: number; h: number } {
  return DEFAULT_SECTION_SIZE[type] ?? FALLBACK_SIZE;
}

/**
 * A full-width stack: every section placed at `x:0` with its per-type size,
 * `y` running down in array order. Identical in visual order to the pre-grid
 * vertical list. Does not read any existing `layout`.
 */
export function deriveDefaultLayout(sections: ReportSectionConfig[]): ReportSectionConfig[] {
  let y = 0;
  return sections.map((section) => {
    const size = defaultSizeForType(section.type);
    const layout: SectionLayout = { x: 0, y, w: size.w, h: size.h };
    y += size.h;
    return { ...section, layout };
  });
}

/**
 * Return a config in which every section has a `layout`. Sections that already
 * carry one are left untouched; sections missing one are appended as a
 * full-width block below the lowest occupied row, so a partially placed
 * composition is completed without moving anything an admin arranged.
 * Idempotent: a fully placed config comes back unchanged (same values).
 */
export function ensureLayout(config: ReportConfig): ReportConfig {
  const anyMissing = config.sections.some((s) => !s.layout);
  if (!anyMissing) return config;

  // Start the back-fill below whatever is already placed.
  let nextY = config.sections.reduce(
    (max, s) => (s.layout ? Math.max(max, s.layout.y + s.layout.h) : max),
    0,
  );

  const sections = config.sections.map((section) => {
    if (section.layout) return section;
    const size = defaultSizeForType(section.type);
    const layout: SectionLayout = { x: 0, y: nextY, w: size.w, h: size.h };
    nextY += size.h;
    return { ...section, layout };
  });

  return { ...config, sections };
}

/**
 * The parity primitive: sections in reading order — `(y, x)` ascending, ties
 * and un-placed sections broken by original array index (a stable sort). A
 * composition with no geometry returns its original array order verbatim, so
 * existing reports and PDFs are byte-identical.
 */
export function linearizeSections(config: ReportConfig): ReportSectionConfig[] {
  return config.sections
    .map((section, index) => ({ section, index }))
    .sort((a, b) => {
      const la = a.section.layout;
      const lb = b.section.layout;
      // Sections without geometry keep array order relative to everything.
      if (!la && !lb) return a.index - b.index;
      if (!la) return a.index - b.index;
      if (!lb) return b.index - a.index === 0 ? 0 : -(b.index - a.index);
      if (la.y !== lb.y) return la.y - lb.y;
      if (la.x !== lb.x) return la.x - lb.x;
      return a.index - b.index;
    })
    .map((entry) => entry.section);
}

/**
 * Group linearized sections into rows for the PDF: consecutive sections whose
 * `y` bands overlap render side-by-side; a section without geometry (or that
 * starts below the current row's bottom) opens a new row. Screen CSS grid and
 * this grouping both consume `linearizeSections`, so their order matches.
 */
export function groupSectionsIntoRows(
  sections: ReportSectionConfig[],
): ReportSectionConfig[][] {
  const rows: ReportSectionConfig[][] = [];
  let currentBottom = -1;

  sections.forEach((section) => {
    const layout = section.layout;
    // No geometry, or a section that begins at/after the current row's bottom,
    // starts a fresh row; otherwise it shares the open row (same y band).
    if (!layout || rows.length === 0 || layout.y >= currentBottom) {
      rows.push([section]);
      currentBottom = layout ? layout.y + layout.h : Number.MAX_SAFE_INTEGER;
    } else {
      rows[rows.length - 1].push(section);
      currentBottom = Math.min(currentBottom, layout.y + layout.h);
    }
  });

  return rows;
}
